export const SUBMITTAL_FEATURE_TYPE = 1;

export type MilestoneDateField = "planned" | "projected" | "actual";

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
  typeof value === "object" && value !== null ? (value as UnknownRecord) : null;

const asArray = (value: unknown): UnknownRecord[] =>
  Array.isArray(value)
    ? value.filter((item): item is UnknownRecord => asRecord(item) !== null)
    : [];

const asText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * The material's own milestones expose `planned`/`projected`/`actual`, while the milestones
 * on a linked submittal use `planned_date`/`projected_date`/`actual_date`. Both shapes occur
 * in the same log payload, so every read tries each.
 */
export const milestoneDateValue = (
  milestone: UnknownRecord | null,
  dateField: MilestoneDateField
): string | null => {
  if (!milestone) return null;
  const direct = milestone[dateField];
  if (typeof direct === "string" && direct) return direct;
  const suffixed = milestone[`${dateField}_date`];
  if (typeof suffixed === "string" && suffixed) return suffixed;
  return null;
};

/** Milestones sit under `date_block.milestones` on the log payload, `milestones` on exports. */
export const readMilestones = (record: unknown): UnknownRecord[] => {
  const source = asRecord(record);
  if (!source) return [];
  const dateBlock = asRecord(source.date_block);
  const fromDateBlock = dateBlock ? asArray(dateBlock.milestones) : [];
  return fromDateBlock.length > 0 ? fromDateBlock : asArray(source.milestones);
};

export const findMilestone = (
  milestones: UnknownRecord[],
  name: string
): UnknownRecord | null =>
  milestones.find((milestone) => milestone.name === name) ?? null;

export const milestoneDate = (
  record: unknown,
  name: string,
  dateField: MilestoneDateField
): string | null =>
  milestoneDateValue(findMilestone(readMilestones(record), name), dateField);

/** Linked submittals arrive as `linked_items` (mixed features) or as `linked_submittals`. */
export const readLinkedSubmittals = (record: unknown): UnknownRecord[] => {
  const source = asRecord(record);
  if (!source) return [];
  const linkedItems = asArray(source.linked_items);
  if (linkedItems.length > 0) {
    return linkedItems.filter(
      (item) =>
        item.feature_type === undefined ||
        item.feature_type === SUBMITTAL_FEATURE_TYPE
    );
  }
  return asArray(source.linked_submittals);
};

export const readLinkedActivities = (record: unknown): UnknownRecord[] => {
  const source = asRecord(record);
  if (!source) return [];
  const tasks = asArray(source.tasks);
  return tasks.length > 0 ? tasks : asArray(source.linked_activities);
};

export const GOVERNING_SUBMITTAL_MILESTONE = "Submittal Distributed";

/**
 * Business rule: when a material has several submittals, the one whose projected
 * "Submittal Distributed" date is latest governs the row. Falls back to the first attached
 * submittal when none of them carry that date.
 */
export const pickGoverningSubmittal = (
  record: unknown
): UnknownRecord | null => {
  const submittals = readLinkedSubmittals(record);
  if (submittals.length === 0) return null;

  let best: UnknownRecord | null = null;
  let bestTime = -Infinity;

  submittals.forEach((submittal) => {
    const value = milestoneDate(
      submittal,
      GOVERNING_SUBMITTAL_MILESTONE,
      "projected"
    );
    const time = value ? Date.parse(value) : Number.NaN;
    if (!Number.isNaN(time) && time > bestTime) {
      best = submittal;
      bestTime = time;
    }
  });

  return best ?? submittals[0];
};

/**
 * The client's sample flagged one activity with `governing_link`, but the live log payload
 * carries no such flag, so critical-path stands in before falling back to the first task.
 * That fallback is an assumption, not a stated requirement — confirm before relying on it.
 */
export const pickGoverningActivity = (
  record: unknown
): UnknownRecord | null => {
  const activities = readLinkedActivities(record);
  if (activities.length === 0) return null;
  return (
    activities.find((activity) => activity.governing_link === true) ??
    activities.find((activity) => activity.is_critical === true) ??
    activities.find((activity) => activity.critical === true) ??
    activities[0]
  );
};

const responsibleContractor = (record: UnknownRecord): string => {
  const nested = asRecord(record.responsible_contractor);
  if (nested) return asText(nested.name);
  return asText(record.responsible_contractor_name);
};

export type MaterialExportRow = {
  materialName: string;
  subcontractor: string;
  risk: string;
  actualFloat: number | null;
  linkedSubmittalCount: number;
  submittedBySCRequired: string | null;
  submittedBySCProjected: string | null;
  submittedBySCActual: string | null;
  submittedForDesignReviewRequired: string | null;
  submittedForDesignReviewProjected: string | null;
  submittedForDesignReviewActual: string | null;
  designReviewCompleteRequired: string | null;
  designReviewCompleteProjected: string | null;
  designReviewCompleteActual: string | null;
  submittalDistributedRequired: string | null;
  submittalDistributedProjected: string | null;
  submittalDistributedActual: string | null;
  materialReleaseRequired: string | null;
  materialReleaseProjected: string | null;
  materialReleaseActual: string | null;
  materialShipActual: string | null;
  materialDeliveryRequired: string | null;
  materialDeliveryProjected: string | null;
  materialDeliveryActual: string | null;
  totalLeadTime: number | null;
  governingActivityName: string;
};

export const mapMaterialRow = (material: unknown): MaterialExportRow => {
  const record = asRecord(material) ?? {};
  const governingSubmittal = pickGoverningSubmittal(record);
  const governingActivity = pickGoverningActivity(record);

  const submittedBySCProjected = milestoneDate(
    governingSubmittal,
    "Submitted by Subcontractor",
    "projected"
  );
  const materialDeliveryProjected = milestoneDate(
    record,
    "Material Delivered",
    "projected"
  );

  const floatValue = record.effective_float ?? record.actual_float;

  return {
    materialName: asText(record.title),
    subcontractor: responsibleContractor(record),
    risk: asText(record.risk_level ?? record.risk),
    actualFloat: typeof floatValue === "number" ? floatValue : null,
    linkedSubmittalCount: readLinkedSubmittals(record).length,

    submittedBySCRequired: milestoneDate(
      governingSubmittal,
      "Submitted by Subcontractor",
      "planned"
    ),
    submittedBySCProjected,
    submittedBySCActual: milestoneDate(
      governingSubmittal,
      "Submitted by Subcontractor",
      "actual"
    ),

    submittedForDesignReviewRequired: milestoneDate(
      governingSubmittal,
      "Submitted for Design Review",
      "planned"
    ),
    submittedForDesignReviewProjected: milestoneDate(
      governingSubmittal,
      "Submitted for Design Review",
      "projected"
    ),
    submittedForDesignReviewActual: milestoneDate(
      governingSubmittal,
      "Submitted for Design Review",
      "actual"
    ),

    designReviewCompleteRequired: milestoneDate(
      governingSubmittal,
      "Design Review Complete",
      "planned"
    ),
    designReviewCompleteProjected: milestoneDate(
      governingSubmittal,
      "Design Review Complete",
      "projected"
    ),
    designReviewCompleteActual: milestoneDate(
      governingSubmittal,
      "Design Review Complete",
      "actual"
    ),

    submittalDistributedRequired: milestoneDate(
      governingSubmittal,
      GOVERNING_SUBMITTAL_MILESTONE,
      "planned"
    ),
    submittalDistributedProjected: milestoneDate(
      governingSubmittal,
      GOVERNING_SUBMITTAL_MILESTONE,
      "projected"
    ),
    submittalDistributedActual: milestoneDate(
      governingSubmittal,
      GOVERNING_SUBMITTAL_MILESTONE,
      "actual"
    ),

    materialReleaseRequired: milestoneDate(
      record,
      "Material Released",
      "planned"
    ),
    materialReleaseProjected: milestoneDate(
      record,
      "Material Released",
      "projected"
    ),
    materialReleaseActual: milestoneDate(record, "Material Released", "actual"),

    materialShipActual: milestoneDate(record, "Material Shipped", "actual"),

    materialDeliveryRequired: milestoneDate(
      record,
      "Material Delivered",
      "planned"
    ),
    materialDeliveryProjected,
    materialDeliveryActual: milestoneDate(
      record,
      "Material Delivered",
      "actual"
    ),

    // Same source the Material Log grid uses for its "Total Lead Time" column.
    totalLeadTime:
      typeof record.lead_time === "number" ? record.lead_time : null,

    governingActivityName: asText(
      governingActivity?.task_name ?? governingActivity?.name
    )
  };
};

export const mapMaterialRows = (materials: unknown[]): MaterialExportRow[] =>
  (Array.isArray(materials) ? materials : []).map(mapMaterialRow);
